import { isEmpty } from "lodash";
import { atom, selector } from "recoil";
import { recoilPersist } from 'recoil-persist';
import { BOOKINGRECIPIENT_SELF, START_STEP, STEP_LOCATION, STEP_SCHEDULE, STEP_SERVICE_BREAKDOWN, STEP_SERVICE_PACKAGES } from "./constants";
import { calculateFromBooking } from "services/createBooking.services";
import { fingerprintState } from "util/deviceUUID";
import { v4 as uuidv4 } from "uuid";
import { anonSessionToken } from "services/anon.service";

const { persistAtom } = recoilPersist()

const userState = atom({
  key: 'userState',
  default: null,
  effects_UNSTABLE: [persistAtom],
});
const otpMobileNumberState = atom({
  key: 'otpMobileNumberState',
  default: null,
  effects_UNSTABLE: [persistAtom],
});

const sessionTokenState = atom({
  key: 'sessionTokenState',
  default: null,
});

const sidebarOpenState = atom({
  key: 'sidebarOpenState',
  default: false,
});

export const DEFAULTLOCATION = {
  "Metro Manila": {
    location: {
      identifier: "Metro Manila",
      latitude: 14.562909599624389,
      longitude: 121.03317570610837,
      isDefaultLocation: true,
    }
  },
  "Metro Cebu": {
    location: {
      identifier: "Metro Cebu",
      latitude: 10.3157,
      longitude: 123.8854,
      isDefaultLocation: true,
    }
  }
}

const bookingRegionAtom = atom({
  key: 'bookingRegionStateAtom',
  default: DEFAULTLOCATION["Metro Manila"],
  effects_UNSTABLE: [persistAtom],
})

const bookingRegionState = selector({
  key: 'bookingRegionState',
  get: ({ get }) => {
    const region = get(bookingRegionAtom)
    return region
  },
  set: ({ set, get }, newValue) => {
    set(bookingRegionAtom, newValue)
    const booking = get(bookingState)

    set(bookingState, {
      ...booking,
      location: newValue.location
    })

  }
})


const bookingState = atom({
  key: 'bookingState',
  default: {
    recipient: BOOKINGRECIPIENT_SELF,
    location: DEFAULTLOCATION["Metro Manila"].location,
  },
  effects_UNSTABLE: [persistAtom],
});

const hasOngoingBookingState = atom({
  key: 'hasOngoingBookingState',
  default: null,
})

const timeSlotState = atom({
  key: 'timeSlotState',
  default: null,
  effects_UNSTABLE: [persistAtom],
});

const packagesAtom = atom({
  key: 'extendedPackagesState',
  default: [],
  effects_UNSTABLE: [persistAtom],
});


const activeServiceState = atom({
  key: 'activeServiceState',
  default: null,
  effects_UNSTABLE: [persistAtom],
});

const servicesAtom = atom({
  key: 'extendedServicesState',
  default: [],
  effects_UNSTABLE: [persistAtom],
});

const servicesState = selector({
  key: 'servicesState',
  get: ({ get }) => {
    const services = get(servicesAtom)
    return services
  },
  set: ({ set, get }, newValue) => {
    set(servicesAtom, newValue)
    set(timeSlotState, null)
    const packages = get(packagesState)
    if (isEmpty(packages) && isEmpty(newValue)) {
      const step = get(stepState)
      // if (step === STEP_SERVICE_PACKAGES) {
      //   set(bookingState, {
      //     ...get(bookingState),
      //     serviceCategory: null
      //   })
      // }
      set(isCartOpenState, false)
    }
  }
})


const cartAtom = atom({
  key: 'cartAtom',
  default: {
    packages: [],
    services: [],
    booking: null
  },
  effects_UNSTABLE: [persistAtom],
})

const cartState = selector({
  key: 'cartState',
  get: ({ get }) => {
    const cart = get(cartAtom)
    return cart
  },
  set: ({ set }, newValue) => {
    set(cartAtom, newValue)
  }
})

const packagesState = selector({
  key: 'packagesState',
  get: ({ get }) => {
    const packages = get(packagesAtom)
    return packages
  },
  set: ({ set, get }, newValue) => {
    set(packagesAtom, newValue)
    set(timeSlotState, null)
    const services = get(servicesState)
    if (isEmpty(services) && isEmpty(newValue)) {
      const step = get(stepState)
      // if (step === STEP_SERVICE_PACKAGES) {
      //   set(bookingState, {
      //     ...get(bookingState),
      //     serviceCategory: null
      //   })
      // }
      set(isCartOpenState, false)
    }
  }
});

const categoriesState = atom({
  key: 'categoriesState',
  default: [],
  effects_UNSTABLE: [persistAtom],
})

const confirmChangeLocationState = atom({
  key: 'confirmChangeLocationState',
  default: false,
});

const stepAtom = atom({
  key: 'stepAtom',
  default: START_STEP,
  effects_UNSTABLE: [persistAtom],
});

const stepState = selector({
  key: 'stepState',
  get: ({ get }) => {
    const step = get(stepAtom)
    return step
  },
  set: ({ set }, newValue) => {
    set(stepAtom, newValue)
    set(isCartOpenState, false)
  }
})

const postLoginStepState = atom({
  key: 'postLoginStepState',
  default: null,
});

const skipStepState = atom({
  key: 'skipStepState',
  default: null,
});

const isCartPrefilledState = atom({
  key: 'isCartPrefilledState',
  default: false,
});

const prefillCartState = atom({
  key: 'prefillCartState',
  default: null,
});

const cartVisibleState = atom({
  key: 'cartVisibleState',
  default: true,
});


const cartCachedState = selector({
  key: 'combinedData',

  get: async ({ get }) => {
    const cart = get(cartState);
    const { packages, services, booking } = cart;

    const fingerprint = get(fingerprintState)
    const session = await anonSessionToken(fingerprint || uuidv4() )
    
    const user = JSON.parse(localStorage.getItem('user'))
    try {
      // Replace this with your actual API call function
      // and pass necessary arguments based on services and packages
      const response = await calculateFromBooking({ 
        booking,
        packages,
        services,
        sessionToken: session,
        clientId: user?.user?.id || user?.client?.id,
      });
      return {
        ...response,
        count: packages?.length + services?.length,
      };
    } catch (error) {
      throw error;
    }
  },
});


const cartLoadingState = atom({
  key: 'cartLoadingState',
  default: false,
});

const isCartOpenAtom = atom({
  key: 'isCartOpenAtom',
  default: false,
});


const isCartOpenState = selector({
  key: 'isCartOpenState',
  get: ({ get }) => {
    const isOpen = get(isCartOpenAtom)
    const packages = get(packagesState)
    const services = get(servicesState)
    if (packages?.length === 0 && services?.length === 0) {
      return false
    } else {
      return isOpen
    }
  },
  set: ({ set }, newValue) => {
    set(isCartOpenAtom, newValue)
  }
})

const autoPopupServiceState = atom({
  key: 'autoPopupServiceState',
  default: null,
});

const cartEmptyState = selector({
  key: 'cartEmptyState',
  get: ({ get }) => {
    const packages = get(packagesState)
    const services = get(servicesState)
    const booking = get(bookingState)
    const emptyCategory = isEmpty(booking?.serviceCategory)
    return packages?.length === 0 && services?.length === 0 && emptyCategory
  }
})

const invalidCartState = atom({
  key: 'invalidCartState',
  default: false,
});

const verifyCartState = ({ packages, newPackages, services, newServices }) => {
  const serviceIds = services.map(service => service.id)
  const newServiceIds = newServices.map(service => service.id)

  //Check if packages are intersecting
  let isIntersecting = false
  let packageIds = []
  let newPackageIds = []
  packages.forEach(element => {
    packageIds = [...packageIds, ...element?.item_ids]
  });

  newPackages.forEach(element => {
    newPackageIds = [...newPackageIds, ...element?.item_ids]
  });
  let intersection = packageIds.filter(x => newPackageIds.includes(x));

  const intersectingpackages = packages.filter(element => {
    return element?.item_ids.some(r => newPackageIds.includes(r))
  })

  return intersectingpackages
}


const cartPackageItemsState = selector({
  key: 'cartPackageItemsState',
  get: ({ get }) => {
    const packages = get(packagesState)
    const services = get(servicesState)

    let packageItems = []
    packages.forEach(element => {
      packageItems = [...packageItems, ...element?.item_ids]
    });
    services.forEach(element => {
      packageItems.push(element?.id)
    })

    return packageItems
  }
})

const getBreadcrumbState = selector({
  key: 'getBreadcrumbState',
  get: ({ get }) => {
    let stepStatuses = [START_STEP]
    // if (!booking?.location?.isDefaultLocation) {
    //   stepStatuses.push(STEP_LOCATION)
    // }
    // if (booking?.serviceCategory) {
    //   stepStatuses.push(STEP_SERVICE_PACKAGES)
    // }
    // if (timeSlot) {
    //   stepStatuses.push(STEP_SCHEDULE)
    // }
    // if (!cartEmpty) {
    //   stepStatuses.push(STEP_SERVICE_BREAKDOWN)
    // }
    return stepStatuses
  }
})

const clearCartState = selector({
  key: 'clearCartState',
  get: ({ get }) => {
    return get(cartEmptyState)
  },
  set: ({ set }, newValue) => {
    set(packagesState, [])
    set(servicesState, [])
    set(cartVisibleState, true)
    set(isCartOpenState, false)
    set(prefillCartState, null)
  }
})

export {
  autoPopupServiceState, bookingState, cartEmptyState, cartPackageItemsState, cartVisibleState, clearCartState, confirmChangeLocationState, getBreadcrumbState, hasOngoingBookingState, invalidCartState, isCartOpenState, isCartPrefilledState, otpMobileNumberState,
  packagesState, prefillCartState, servicesState, sessionTokenState, sidebarOpenState, skipStepState, stepState, timeSlotState, userState, verifyCartState,
  postLoginStepState,
  bookingRegionState,
  categoriesState,
  activeServiceState,
  cartLoadingState,
  cartCachedState,
  cartState,
};
